import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Bell, MessageSquare, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';

function timeAgo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const basePath = isAdmin ? '/admin' : '/partner';

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = () => {
    setLoading(true);
    apiGet('/notifications')
      .then(r => setNotifications(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Failed to load notifications'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchNotifications(); }, []);

  const handleOpen = async (notif) => {
    if (!notif.read) {
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      apiPost(`/notifications/${notif.id}/read`, {}).then(() => {
        window.dispatchEvent(new CustomEvent('xp:refresh-notifications'));
      }).catch(() => {});
    }
    if (notif.type === 'chat') {
      window.dispatchEvent(new CustomEvent('xp:open-chat', { detail: { peerId: notif.application_id } }));
      return;
    }
    if (notif.type === 'loan_status' || notif.type === 'loan_request') { navigate(`${basePath}/loans`); return; }
    if (notif.type === 'message' || notif.type === 'message_reply') { navigate(`${basePath}/messages`); return; }
    navigate(`${basePath}/applications/${notif.application_id}`);
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await apiPost('/notifications/read-all', {});
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      window.dispatchEvent(new CustomEvent('xp:refresh-notifications'));
    } catch {
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="notifications-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-ember tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Bell className="w-6 h-6 text-brand" /> Notifications
          </h1>
          <p className="text-ink-2 mt-1 text-sm">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'You are all caught up'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" onClick={handleMarkAllRead} disabled={markingAll} className="gap-2 text-sm">
            <CheckCheck className="w-4 h-4" /> {markingAll ? 'Marking…' : 'Mark all as read'}
          </Button>
        )}
      </div>

      <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 shadow-card">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-16 text-center">
            <Bell className="w-10 h-10 text-ink-3 mx-auto mb-3" />
            <p className="text-ink-2 font-medium">No notifications yet</p>
            <p className="text-ink-3 text-sm mt-1">Updates about applications, students, loans, messages and chat will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-contrast/[0.07]">
            {notifications.map(n => (
              <button
                key={n.id}
                onClick={() => handleOpen(n)}
                className={`w-full text-left flex items-start gap-3 px-5 py-4 hover:bg-contrast/5 transition-colors ${!n.read ? 'bg-brand/[0.04]' : ''}`}
                data-testid={`notification-${n.id}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${n.read ? 'bg-contrast/[0.07]' : 'bg-surface-3'}`}>
                  <MessageSquare className={`w-4 h-4 ${n.read ? 'text-ink-3' : 'text-brand'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm ${n.read ? 'text-ink-2' : 'font-semibold text-ink'}`}>
                      {n.type === 'chat'
                        ? `${n.from_name || (n.from_role === 'admin' ? 'Admin' : 'Partner')} sent a live chat message`
                        : n.type === 'loan_request'
                          ? `New loan request from ${n.from_name || 'a partner'} for ${n.candidate_name || 'a student'}${n.text ? ` (${n.text})` : ''}`
                        : n.type === 'loan_status'
                          ? `Loan application status changed to ${n.text}`
                          : n.type === 'message'
                            ? `New message from ${n.from_name || 'Partner'}: ${n.application_number || ''}`
                            : n.type === 'message_reply'
                              ? `Admin replied to: ${n.application_number || 'Message'}`
                              : `${n.from_name || (n.from_role === 'admin' ? 'Admin' : 'Partner')} ${n.type === 'comment' ? 'commented on' : 'updated'} ${n.application_number || 'an application'}`}
                    </span>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-brand shrink-0" />}
                  </div>
                  {n.candidate_name && (
                    <p className="text-xs text-ink-3 mt-0.5">Candidate: {n.candidate_name}</p>
                  )}
                  <p className="text-sm text-ink mt-1 truncate">{n.text}</p>
                  <p className="text-xs text-ink-3 mt-1">{timeAgo(n.created_at)}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
